import _ from 'underscore';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { Activity, Message } from 'botframework-directlinejs';
import Markdown from 'markdown-to-jsx';
import { Button, mergeClasses } from '@fluentui/react-components';
import { ArrowCircleRight20Regular } from '@fluentui/react-icons';
import { I18nContext } from '@bingads-webui/copilot-i18n';
import { type ILogger } from '@bingads-webui-react/msa-copilot-common';

import { InsightsChart } from '../insights-chart';
import { useInsightsPanelPreviewStyles } from './use-insights-panel-preview-styles';
import { useFluctuationsMessage } from '../../hooks/use-fluctuations-message';

export type InsightsPanelPreviewProps = {
  activity: Activity;
  logger?: ILogger;
  onFullInsightsClick: (ev: React.MouseEvent<HTMLButtonElement>, replyToId: string | undefined) => void;
};

/**
 * InsightsPanelPreview is the smaller card insight the chat stream. It
 * displays a short summary of the insight data received from the bot and
 * provides buttons for the user to navigate to the full insights panel.
 */
export const InsightsPanelPreview: React.FC<InsightsPanelPreviewProps> = React.memo((props) => {
  const i18n = useContext(I18nContext);
  const classes = useInsightsPanelPreviewStyles();

  const { activity, logger, onFullInsightsClick } = props;
  const message = activity as Message;

  const replyToId: string | undefined = (message as any)?.replyToId;

  const { fluctuationsChartData, fluctuationResult, isCompleted } = useFluctuationsMessage(replyToId);

  useEffect(() => {
    if (logger && isCompleted) {
      logger?.trace(`InsightsPanelPreview: replyToId: ${replyToId}, fluctuationResult.Result.length: ${_.size(fluctuationResult?.Result)}, fluctuationsChartData: ${!!fluctuationsChartData}`);
    }
  }, [
    fluctuationResult?.Result,
    fluctuationsChartData,
    isCompleted,
    logger,
    replyToId,
  ]);

  const parseDate = useMemo(() => i18n.dateParser({ raw: 'yyyy-MM-dd' }), [i18n]);

  const headerText = useMemo<string | undefined>(() => {
    if (fluctuationResult?.Result) {
      const fluctuation = fluctuationResult.Result[0];

      if (fluctuation) {
        let date = '';
        try {
          const dateObj = parseDate(fluctuation.shiftStartDate);
          date = i18n.formatDate(dateObj);
        } catch (e) {
          // do nothing
        }

        const isDecrease = fluctuation.shiftDirection === 'Decrease';

        let tlKey;
        switch (fluctuation.metricType) {
          case 'Impression':
            tlKey = isDecrease ? _TL_('Impressions decreased on {{date}}') : _TL_('Impressions increased on {{date}}');
            break;
          case 'Conversion':
            tlKey = isDecrease ? _TL_('Conversions decreased on {{date}}') : _TL_('Conversions increased on {{date}}');
            break;
          default:
            tlKey = isDecrease ? _TL_('Clicks decreased on {{date}}') : _TL_('Clicks increased on {{date}}');
            break;
        }

        return i18n?.getString(tlKey, { date });
      }
    }
    return undefined;
  }, [fluctuationResult, i18n, parseDate]);

  const handleFullInsightsClick = useCallback((ev: React.MouseEvent<HTMLButtonElement>) => {
    logger?.trace(`InsightsPanelPreview: Click on View Details, replyToId: ${replyToId}`);
    onFullInsightsClick(ev, replyToId);
  }, [logger, onFullInsightsClick, replyToId]);

  return (
    <div className={mergeClasses(classes.root, 'insights-panel-preview')}>
      <div className={mergeClasses(classes.top, 'insights-panel-preview-top')}>
        <div className={mergeClasses(classes.header, 'insights-panel-preview-header')}>
          {i18n?.getString(_TL_('Fluctuations'))}
        </div>
      </div>

      {/* display the chart for the first fluctuation */}
      <div className={classes.chart}>
        {headerText && (
          <div className={mergeClasses(classes.chartHeader, 'insights-panel-preview-chart-header')}>{headerText}</div>
        )}
        <InsightsChart
          isPreview
          isLoading={!fluctuationsChartData && !isCompleted}
          performanceTimeSeries={fluctuationsChartData?.[0]}
          TimeZone={71}
        />
      </div>

      <div className={mergeClasses(classes.summary, 'insights-panel-preview-summary')}>
        <Markdown
          className={mergeClasses(classes.summaryContent, 'insights-panel-preview-summary-content')}
          options={{ forceInline: true }}
        >
          {message.text || ''}
        </Markdown>
      </div>

      <div className="insights-panel-preview-buttons">
        <Button
          className={mergeClasses(classes.goFullButton, 'insights-panel-preview-go-full-button')}
          size="small"
          disabled={replyToId == null || !fluctuationsChartData}
          onClick={handleFullInsightsClick}
          icon={<ArrowCircleRight20Regular />}
        >
          {i18n?.getString(_TL_('View details'))}
        </Button>
      </div>
    </div>
  );
});
